/**
 * The Dual model Employee + IC GTM events: the client's tracking spec for the IC and EM application flows of IC-model
 * countries, sent next to the legacy events.
 *
 * Every event goes through `pushModelEvent()`, which reuses the event push of the GoogleTagManager
 * instance, so the Dual model Employee + IC events share the queue and consent handling of the legacy events.
 */
import { googleTagManager } from './index';
import { getSessionItem, setSessionItem } from '../session-storage';

export const BUSINESS_MODEL = Object.freeze({ IC: 'IC', EM: 'EM' });

// GTM `page_group` of the Dual model Employee + IC screens
export const MODEL_PAGE_GROUP = Object.freeze({
    GENERAL_LANDING: 'General landing page',
    LANDING: 'Landing page',
    WHAT_YOU_NEED: 'What you need',
    CITY_SELECTION: 'City selection',
    AREA_SELECTION: 'Area selection',
    DELIVERY_AREA_FULL: 'Delivery area full',
    PERSONAL_INFORMATION: 'Personal information',
    REFERRAL: 'Personal information: referral',
    AGE: 'Personal information: age',
    HOURS: 'Schedule selection: hours',
    SHIFTS: 'Schedule selection: shifts',
    VEHICLE_SELECTION: 'Vehicle selection',
    APPLICATION_SUMMARY: 'Application summary',
    APPLICATION_SUMMARY_UNDER_AGE: 'Application summary: under legal age',
    APPLICATION_PROCESSING: 'Application processing',
    APPLICATION_SUBMITTED: 'Application submitted',
    APPLICATION_SUBMITTED_SUCCESS: 'Application submitted: success',
    APPLICATION_SUBMITTED_REJECTED: 'Application submitted: rejected',
    APPLICATION_SUBMITTED_SF_FAILED: 'Application submitted: sf_failed',
    WAITLIST_PERSONAL_INFORMATION: 'Waitlist: personal information',
    WAITLIST_VEHICLE_SELECTION: 'Waitlist: vehicle selection',
    WAITLIST_APPLICATION_SUMMARY: 'Waitlist: application summary',
    WAITLIST_APPLICATION_SUBMITTED: 'Waitlist: application submitted',
});

// Status of a Dual model Employee + IC event in the dev console log
const MODEL_STATUS = Object.freeze({
    SENT: 'SENT',
    QUEUED: 'QUEUED (waiting for the cookie banner)',
    GTM_DISABLED: 'GTM disabled (dataLayer only)',
    NO_EVENT: 'NO EVENT for this screen or outcome',
    DUPLICATE: 'SKIPPED: duplicate application_id',
});

// GTM `page_environment` of each APP_ENV
const MODEL_PAGE_ENVIRONMENTS = Object.freeze({
    local: 'dev',
    dev: 'dev',
    testing: 'dev',
    acceptance: 'acceptance',
    review: 'acceptance',
    staging: 'staging',
    production: 'production',
});

// Query parameters kept in original_referrer / original_location; everything else (email, tokens, ...) is dropped
const MODEL_URL_QUERY_ALLOWLIST = ['utm_*', 'gclid', 'fbclid', 'city', 'page', 'campaign', 'flow'];

// Employee form questions with a screen of their own (other questions, e.g. driving_license, have no page view)
const MODEL_EM_QUESTION_PAGE_GROUPS = Object.freeze({
    age: MODEL_PAGE_GROUP.AGE,
    agespec: MODEL_PAGE_GROUP.AGE,
    working_hours: MODEL_PAGE_GROUP.HOURS,
    picked_shift: MODEL_PAGE_GROUP.SHIFTS,
    vehicle_type: MODEL_PAGE_GROUP.VEHICLE_SELECTION,
});

// Outcome of a submission; each sends its own application_submitted (the IC waitlist sends none)
const MODEL_OUTCOME = Object.freeze({
    IC_SUCCESS: 'ic_success',
    IC_WAITLIST: 'ic_waitlist',
    EM_SUCCESS: 'em_success',
    EM_CLOSED_CITY: 'em_closed_city',
    EM_UNDER_AGE: 'em_under_age',
});

// Session handoffs; outcomes and application ids only, never personal data
const MODEL_OUTCOME_KEY = 'model_tracking_outcome';
const MODEL_TRACKED_SUBMISSIONS_KEY = 'model_tracked_submissions';

//Last Dual model Employee + IC screen tracked on this page (re-renders of the same screen are one page view)
let lastModelScreen = null;
//Whether the Dual model Employee + IC events are logged to the console on this page (dev only, evaluated once)
let modelDebugEnabled = null;

/**
 * Gate for the Dual model Employee + IC events: the country uses the IC model (`GlobalDataProvider::useModelTracking()`).
 * @returns {boolean}
 */
export function usesModelTracking() {
    return window.useModelTracking === true;
}

/**
 * Dual model per city country: its own events next to the shared ones (city_model_click, apply_now_click per model, zones).
 * @returns {boolean}
 */
export function isPerCityTracking() {
    return window.country?.delivery_model_mode === 'per_city';
}

/**
 * The business_model of a delivery model (window.pageDeliveryModel, a city's delivery_model).
 * @param {string|null} deliveryModel - ic or employee.
 * @returns {string|null} One of BUSINESS_MODEL.
 */
export function businessModelOf(deliveryModel) {
    return { ic: BUSINESS_MODEL.IC, employee: BUSINESS_MODEL.EM }[deliveryModel] ?? null;
}

/**
 * Dual model per city model page or city page: the page view and apply_now_click of its model.
 * @returns {string|null} One of BUSINESS_MODEL; null on other pages.
 */
export function perCityLandingBusinessModel() {
    return isPerCityTracking() && ['model', 'city'].includes(window.pageKind)
        ? businessModelOf(window.pageDeliveryModel)
        : null;
}

/**
 * The page_environment of this APP_ENV: dev, acceptance, staging or production.
 * @returns {string}
 */
function getModelPageEnvironment() {
    return MODEL_PAGE_ENVIRONMENTS[window.appEnvironment] ?? window.appEnvironment;
}

/**
 * Keep origin and path plus allowlisted query parameters, drop everything else (email, tokens,
 * Salesforce ids, ...) and the hash.
 * @param {string|null} url
 * @returns {string|null}
 */
function sanitiseTrackingUrl(url) {
    if (!url) {
        return null;
    }

    let parsed;
    try {
        parsed = new URL(url);
    } catch {
        return null;
    }

    const isAllowed = (name) =>
        MODEL_URL_QUERY_ALLOWLIST.some((entry) =>
            entry.endsWith('*') ? name.startsWith(entry.slice(0, -1)) : name === entry,
        );

    const kept = new URLSearchParams();
    parsed.searchParams.forEach((value, name) => {
        if (isAllowed(name)) {
            kept.append(name, value);
        }
    });
    const query = kept.toString();

    return parsed.origin + parsed.pathname + (query ? `?${query}` : '');
}

/**
 * Referrer and URL of the first page of the session, as stored by the GoogleTagManager's
 * captureOriginalData() (also run here when GTM is disabled, e.g. on localhost), sanitised.
 * @returns {{ original_referrer: string|null, original_location: string|null }}
 */
function getModelOriginalData() {
    if (getSessionItem('original_location') === null) {
        googleTagManager.captureOriginalData();
    }

    return {
        original_referrer: sanitiseTrackingUrl(getSessionItem('original_referrer') || null),
        original_location: sanitiseTrackingUrl(getSessionItem('original_location') || null),
    };
}

/**
 * Values every Dual model Employee + IC event sends, like setPageData() for the legacy events; each event adds its own.
 * @param {string} pageGroup - One of MODEL_PAGE_GROUP.
 * @param {string|null} businessModel - One of BUSINESS_MODEL; none on the general landing page.
 * @returns {Object}
 */
function getModelPageData(pageGroup, businessModel) {
    return {
        page_group: pageGroup,
        ...(businessModel && { business_model: businessModel }),
        country: window.country.code,
        page_environment: getModelPageEnvironment(),
    };
}

/**
 * Log a Dual model Employee + IC event to the console, to check the events by hand on localhost (where GTM is disabled).
 * Only in the dev environment (APP_ENV local / dev / testing); logs only what goes into the dataLayer.
 * @param {string} eventName
 * @param {Object|null} customData
 * @param {string} status - One of MODEL_STATUS.
 */
function logModelEvent(eventName, customData, status) {
    if (modelDebugEnabled === null) {
        modelDebugEnabled = getModelPageEnvironment() === 'dev';
        if (modelDebugEnabled) {
            window.modelEvents = [];
        }
    }
    if (!modelDebugEnabled) {
        return;
    }

    const pageGroup = customData?.page_group ?? '—';
    const businessModel = customData?.business_model ?? '—';
    window.modelEvents.push({
        at: new Date().toISOString(),
        event: eventName,
        page_group: pageGroup,
        status,
        customData,
    });

    /* eslint-disable no-console */
    console.groupCollapsed(`[Model tracking] ${eventName} · "${pageGroup}" · ${businessModel} — ${status}`);
    if (customData) {
        console.table(customData);
    }
    console.groupEnd();
    /* eslint-enable no-console */
}

/**
 * Push a Dual model Employee + IC event through the same queue and consent handling as the legacy events (held until the
 * cookie banner is answered), and log it. The data is built by the caller, so a queued push keeps the
 * values of this moment.
 * @param {string} eventName
 * @param {Object} customData
 * @param {Object} [options]
 * @param {boolean} [options.awaited] - Resolve only once GTM fired the tags (capped), for events before a redirect.
 * @returns {Promise<void>}
 */
function pushModelEvent(eventName, customData, { awaited = false } = {}) {
    let status = MODEL_STATUS.SENT;
    if (!window.gtmTrackingEnabled) {
        status = MODEL_STATUS.GTM_DISABLED;
    } else if (!googleTagManager.hasCookieConsentForNewBanner()) {
        status = MODEL_STATUS.QUEUED;
    }
    logModelEvent(eventName, customData, status);

    const options = { customData, addUtmData: false, includePageData: false };
    if (awaited) {
        return googleTagManager.pushEventAwaited(eventName, options);
    }
    googleTagManager.pushEvent(eventName, options);
    return Promise.resolve();
}

/**
 * Dual model Employee + IC page_view of a screen; a re-render of the same screen is not a new page view.
 * @param {string|null} pageGroup - One of MODEL_PAGE_GROUP; null for a screen without a page view.
 * @param {string|null} businessModel - One of BUSINESS_MODEL; none on the general landing page.
 */
export function trackModelPageView(pageGroup, businessModel) {
    if (!usesModelTracking()) {
        return;
    }
    if (!pageGroup) {
        logModelEvent('page_view', null, MODEL_STATUS.NO_EVENT);
        return;
    }

    const screen = `${pageGroup}|${businessModel}`;
    if (screen === lastModelScreen) {
        return;
    }
    lastModelScreen = screen;

    pushModelEvent('page_view', {
        ...getModelPageData(pageGroup, businessModel),
        page_language: window.language.code,
        ...getModelOriginalData(),
    });
}

/**
 * Dual model Employee + IC business_model_click on a hero CTA of the general landing page.
 * @param {string} businessModel - One of BUSINESS_MODEL, the model of the clicked CTA.
 */
export function trackBusinessModelClick(businessModel) {
    if (!usesModelTracking()) {
        return;
    }

    pushModelEvent('business_model_click', getModelPageData(MODEL_PAGE_GROUP.GENERAL_LANDING, businessModel));
}

/**
 * Dual model per city city_model_click: a city picked on the general landing page and its CTA clicked.
 * @param {string} businessModel - One of BUSINESS_MODEL, the model of the picked city.
 */
export function trackCityModelClick(businessModel) {
    if (!usesModelTracking()) {
        return;
    }

    pushModelEvent('city_model_click', getModelPageData(MODEL_PAGE_GROUP.GENERAL_LANDING, businessModel));
}

/**
 * apply_now_click on a CTA that opens the form: the IC landing page, in Dual model per city the IC and EM pages and city pages.
 * @param {string} [businessModel] - One of BUSINESS_MODEL.
 */
export function trackModelApplyNowClick(businessModel = BUSINESS_MODEL.IC) {
    if (!usesModelTracking()) {
        return;
    }

    pushModelEvent('apply_now_click', getModelPageData(MODEL_PAGE_GROUP.LANDING, businessModel));
}

/**
 * Dual model Employee + IC page_group of the multi-step form screen, like `step_tracking_name` for the legacy pageGroup.
 * @param {Object} screen
 * @param {boolean} screen.icFlow
 * @param {string} screen.step - The step id (location, what_you_need, personal_info, vehicle, complete).
 * @param {number|null} screen.fixedSubstep - Index of the fixed substep, null on a form question.
 * @param {string|null} screen.question - data_key of the form question on screen.
 * @param {boolean} screen.closedLocation - Closed IC zone or inactive employee city (waitlist).
 * @param {string} screen.dropoff - none, age or other (a rejecting answer).
 * @returns {string|null} One of MODEL_PAGE_GROUP; null for a screen without a page view.
 */
export function getModelFormPageGroup({ icFlow, step, fixedSubstep, question, closedLocation, dropoff }) {
    if (step === 'location') {
        // Dual model per city: the IC zones, a city in the Employee form
        return isPerCityTracking() ? MODEL_PAGE_GROUP.AREA_SELECTION : MODEL_PAGE_GROUP.CITY_SELECTION;
    }
    if (step === 'what_you_need') {
        return MODEL_PAGE_GROUP.WHAT_YOU_NEED;
    }

    if (icFlow) {
        if (step === 'personal_info') {
            return closedLocation
                ? MODEL_PAGE_GROUP.WAITLIST_PERSONAL_INFORMATION
                : MODEL_PAGE_GROUP.PERSONAL_INFORMATION;
        }
        if (step === 'vehicle') {
            return closedLocation ? MODEL_PAGE_GROUP.WAITLIST_VEHICLE_SELECTION : MODEL_PAGE_GROUP.VEHICLE_SELECTION;
        }
        if (step === 'complete') {
            return closedLocation
                ? MODEL_PAGE_GROUP.WAITLIST_APPLICATION_SUMMARY
                : MODEL_PAGE_GROUP.APPLICATION_SUMMARY;
        }
        return null;
    }

    if (step === 'personal_info' && fixedSubstep === 0) {
        return MODEL_PAGE_GROUP.PERSONAL_INFORMATION;
    }
    if (step === 'personal_info' && fixedSubstep === 1) {
        return MODEL_PAGE_GROUP.REFERRAL;
    }
    if (step === 'complete') {
        return dropoff === 'age'
            ? MODEL_PAGE_GROUP.APPLICATION_SUMMARY_UNDER_AGE
            : MODEL_PAGE_GROUP.APPLICATION_SUMMARY;
    }

    return MODEL_EM_QUESTION_PAGE_GROUPS[question] ?? null;
}

/**
 * The outcome of a submission, or null for a rejection without a Dual model Employee + IC event (a drop-off other than under age).
 * @param {{ icFlow: boolean, closedLocation: boolean, dropoff: string }} submission
 * @returns {string|null} One of MODEL_OUTCOME.
 */
export function getModelOutcome({ icFlow, closedLocation, dropoff }) {
    if (icFlow) {
        return closedLocation ? MODEL_OUTCOME.IC_WAITLIST : MODEL_OUTCOME.IC_SUCCESS;
    }
    if (closedLocation) {
        return MODEL_OUTCOME.EM_CLOSED_CITY;
    }
    if (dropoff === 'age') {
        return MODEL_OUTCOME.EM_UNDER_AGE;
    }

    return dropoff === 'none' ? MODEL_OUTCOME.EM_SUCCESS : null;
}

/**
 * Dual model Employee + IC application_submitted, at most once per application_id; also remembers the outcome for the
 * thank-you page view after the redirect. A success without a Salesforce applicant id is sent as
 * sf_failed, a rejected application sends no applicant_id, an under-age one no vehicle_type either,
 * and the IC waitlist sends no event.
 * @param {string|null} outcome - One of MODEL_OUTCOME; null for a rejection without an event.
 * @param {Object} application
 * @param {string|null} application.city - tracking_name of the selected city.
 * @param {string|null} application.zone - Selected IC zone (zone_name), in Dual model per city the zone names joined by a comma; only sent on an IC application.
 * @param {string|null} application.vehicleType
 * @param {string|null} application.applicantId - Salesforce applicant id from the apply response.
 * @param {string|null} application.applicationId - Server-issued application_id from the apply response.
 * @returns {Promise<void>} Resolves when the event's tags executed or the cap elapsed; safe to await before a redirect.
 */
export function trackModelApplicationSubmitted(outcome, { city, zone, vehicleType, applicantId, applicationId }) {
    if (!usesModelTracking()) {
        return Promise.resolve();
    }
    setSessionItem(MODEL_OUTCOME_KEY, outcome);

    const isSuccess = outcome === MODEL_OUTCOME.IC_SUCCESS || outcome === MODEL_OUTCOME.EM_SUCCESS;
    // A success only counts once Salesforce returned the applicant id
    const sfFailed = isSuccess && !applicantId;

    let pageData;
    switch (outcome) {
        case MODEL_OUTCOME.IC_SUCCESS:
            pageData = getModelPageData(
                sfFailed ? MODEL_PAGE_GROUP.APPLICATION_SUBMITTED_SF_FAILED : MODEL_PAGE_GROUP.APPLICATION_SUBMITTED,
                BUSINESS_MODEL.IC,
            );
            break;
        case MODEL_OUTCOME.EM_SUCCESS:
            pageData = getModelPageData(
                sfFailed
                    ? MODEL_PAGE_GROUP.APPLICATION_SUBMITTED_SF_FAILED
                    : MODEL_PAGE_GROUP.APPLICATION_SUBMITTED_SUCCESS,
                BUSINESS_MODEL.EM,
            );
            break;
        case MODEL_OUTCOME.EM_CLOSED_CITY:
        case MODEL_OUTCOME.EM_UNDER_AGE:
            pageData = getModelPageData(MODEL_PAGE_GROUP.APPLICATION_SUBMITTED_REJECTED, BUSINESS_MODEL.EM);
            break;
        default:
            logModelEvent('application_submitted', null, MODEL_STATUS.NO_EVENT);
            return Promise.resolve();
    }

    // One event per application, also when the page is re-rendered or the response replayed
    let trackedSubmissions;
    try {
        trackedSubmissions = JSON.parse(getSessionItem(MODEL_TRACKED_SUBMISSIONS_KEY) ?? '[]');
    } catch {
        trackedSubmissions = [];
    }
    if (applicationId && trackedSubmissions.includes(applicationId)) {
        logModelEvent('application_submitted', pageData, MODEL_STATUS.DUPLICATE);
        return Promise.resolve();
    }
    if (applicationId) {
        setSessionItem(MODEL_TRACKED_SUBMISSIONS_KEY, JSON.stringify([...trackedSubmissions, applicationId]));
    }

    return pushModelEvent(
        'application_submitted',
        {
            ...pageData,
            application_city: city ?? null,
            ...(outcome === MODEL_OUTCOME.IC_SUCCESS && { application_zone: zone ?? null }),
            ...(outcome !== MODEL_OUTCOME.EM_UNDER_AGE && { vehicle_type: vehicleType ?? null }),
            ...(isSuccess && { applicant_id: applicantId ?? null }),
            application_id: applicationId ?? null,
        },
        { awaited: true },
    );
}

/**
 * Dual model Employee + IC page_view of the thank-you page, for the outcome remembered before the redirect (removed on
 * read so it cannot leak into a later visit; a successful application goes to the Salesforce portal).
 */
export function trackModelThankYou() {
    if (!usesModelTracking()) {
        return;
    }

    const outcome = getSessionItem(MODEL_OUTCOME_KEY);
    setSessionItem(MODEL_OUTCOME_KEY, null);

    switch (outcome) {
        case MODEL_OUTCOME.IC_WAITLIST:
            trackModelPageView(MODEL_PAGE_GROUP.WAITLIST_APPLICATION_SUBMITTED, BUSINESS_MODEL.IC);
            break;
        case MODEL_OUTCOME.EM_CLOSED_CITY:
        case MODEL_OUTCOME.EM_UNDER_AGE:
            trackModelPageView(MODEL_PAGE_GROUP.APPLICATION_SUBMITTED_REJECTED, BUSINESS_MODEL.EM);
            break;
        // A success only lands here when Salesforce failed; otherwise it goes to the Salesforce portal
        case MODEL_OUTCOME.IC_SUCCESS:
            trackModelPageView(MODEL_PAGE_GROUP.APPLICATION_SUBMITTED_SF_FAILED, BUSINESS_MODEL.IC);
            break;
        case MODEL_OUTCOME.EM_SUCCESS:
            trackModelPageView(MODEL_PAGE_GROUP.APPLICATION_SUBMITTED_SF_FAILED, BUSINESS_MODEL.EM);
            break;
    }
}
